/**
 * Hierarchy roles — what an Overseer, Bishop or Archbishop may reach.
 *
 * One row per rung of the church ladder. A super admin sets the church
 * permissions, the platform capabilities, and how far the office reaches:
 * its own unit, everything beneath it, or a named list of provinces,
 * campuses and churches. Saved is the truth; every row can be reset back to
 * the platform default.
 */
import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Loader2, ShieldCheck, RotateCcw, Layers, Building2 } from 'lucide-react';
import { authHeaders } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = async (path, options = {}) => {
  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(options.headers || {}) },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.detail || 'Something went wrong');
  return body;
};

const SCOPE_LABEL = {
  own_unit: 'Own unit only',
  unit_and_below: 'Unit and everything below',
  explicit: 'Named provinces, campuses and churches',
};

export default function AdminHierarchyRoles() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['office-roles'],
    queryFn: () => api('/api/superadmin/office-roles'),
  });
  const { data: cat } = useQuery({
    queryKey: ['office-roles-catalogue'],
    queryFn: () => api('/api/superadmin/office-roles/catalogue'),
  });

  const reset = useMutation({
    mutationFn: (office) => api(`/api/superadmin/office-roles/${office}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Back to the platform default'); qc.invalidateQueries({ queryKey: ['office-roles'] }); },
    onError: (e) => toast.error(e.message),
  });

  const offices = data?.offices || [];

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-6" data-testid="hierarchy-roles-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Hierarchy roles</h1>
        <p className="text-sm text-slate-500 mt-1">
          Decide exactly what each office carries and how far it reaches. Holders pick the change
          up on their next sign-in.
        </p>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : (
        <div className="space-y-3">
          {offices.map(o => (
            <Card key={o.office} data-testid={`office-row-${o.office}`}>
              <CardHeader className="pb-2">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <CardTitle className="text-base flex items-center gap-2">
                      <ShieldCheck className="h-4 w-4 text-indigo-500" />
                      {o.label}
                      {o.is_overridden && <Badge variant="outline" className="text-[10px]">configured</Badge>}
                    </CardTitle>
                    <p className="text-xs text-slate-500 mt-1">
                      {o.level || '—'} · {o.tier || '—'} · {SCOPE_LABEL[o.scope_mode] || o.scope_mode}
                      {o.scope_mode === 'explicit' && ` (${(o.scope_church_ids || []).length})`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {o.is_overridden && (
                      <Button variant="ghost" size="sm" onClick={() => reset.mutate(o.office)}
                              data-testid={`office-reset-${o.office}`}>
                        <RotateCcw className="h-3.5 w-3.5 mr-1" /> Reset
                      </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={() => setEditing(o)}
                            data-testid={`office-configure-${o.office}`}>
                      Configure
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="flex flex-wrap gap-1">
                  {(o.permissions || []).map(p => (
                    <span key={p} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{p}</span>
                  ))}
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700"
                        data-testid={`office-cap-count-${o.office}`}>
                    <Layers className="h-3 w-3 inline mr-1" />{(o.capabilities || []).length} capabilities
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {editing && cat && (
        <OfficeDialog office={editing} catalogue={cat} onClose={() => setEditing(null)}
                      onSaved={() => { qc.invalidateQueries({ queryKey: ['office-roles'] }); setEditing(null); }} />
      )}
    </div>
  );
}

function OfficeDialog({ office, catalogue, onClose, onSaved }) {
  const [perms, setPerms] = useState(office.permissions || []);
  const [caps, setCaps] = useState(office.capabilities || []);
  const [mode, setMode] = useState(office.scope_mode || 'own_unit');
  const [units, setUnits] = useState(office.scope_church_ids || []);
  const [q, setQ] = useState('');
  const [unitQ, setUnitQ] = useState('');

  const toggle = (list, setList, key) =>
    setList(list.includes(key) ? list.filter(x => x !== key) : [...list, key]);

  const term = q.trim().toLowerCase();
  const modules = useMemo(() => (catalogue.modules || [])
    .map(m => ({ ...m, matches: term ? m.capabilities.filter(c => c.includes(term)) : m.capabilities }))
    .filter(m => m.matches.length > 0), [catalogue.modules, term]);
  const unitTerm = unitQ.trim().toLowerCase();
  const unitRows = (catalogue.units || []).filter(u => !unitTerm || u.name.toLowerCase().includes(unitTerm));

  const save = useMutation({
    mutationFn: () => api(`/api/superadmin/office-roles/${office.office}`, {
      method: 'PUT',
      body: JSON.stringify({ permissions: perms, capabilities: caps,
                             scope_mode: mode, scope_church_ids: units }),
    }),
    onSuccess: () => { toast.success(`${office.label} updated`); onSaved?.(); },
    onError: (e) => toast.error(e.message),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="office-dialog">
        <DialogHeader>
          <DialogTitle>{office.label}</DialogTitle>
          <DialogDescription>
            What this office may do, and how far it reaches. Whatever you save is what the holder
            gets — you can take things away as well as add them.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div>
            <Label>Church permissions</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {(catalogue.permission_words || []).map(w => (
                <button key={w} type="button" onClick={() => toggle(perms, setPerms, w)}
                        className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${perms.includes(w) ? 'border-emerald-300 bg-emerald-50 text-emerald-800' : 'border-slate-200 text-slate-600'}`}
                        data-testid={`office-perm-${w}`}>
                  {w.replace(/_/g, ' ')}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between gap-2">
              <Label>Platform capabilities <span className="font-normal text-slate-400">({caps.length} of {catalogue.total_capabilities})</span></Label>
              <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search capabilities…"
                     className="h-8 w-44 text-xs" data-testid="office-cap-search" />
            </div>
            <div className="mt-2 max-h-64 overflow-y-auto space-y-2 pr-1">
              {modules.map(m => (
                <div key={m.module} className="rounded-lg border border-slate-200 p-2" data-testid={`office-cap-module-${m.module}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-slate-700">{m.module}</span>
                    <button type="button" className="text-[10px] text-indigo-600"
                            onClick={() => {
                              const all = m.matches.every(c => caps.includes(c));
                              setCaps(all ? caps.filter(c => !m.matches.includes(c))
                                          : Array.from(new Set([...caps, ...m.matches])));
                            }}
                            data-testid={`office-cap-module-all-${m.module}`}>
                      {m.matches.every(c => caps.includes(c)) ? 'clear' : 'select all'}
                    </button>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-1 mt-1">
                    {m.matches.map(c => (
                      <label key={c} className="flex items-center gap-2 text-[11px] text-slate-600 cursor-pointer"
                             data-testid={`office-cap-${c}`}>
                        <input type="checkbox" checked={caps.includes(c)}
                               onChange={() => toggle(caps, setCaps, c)} />
                        {c}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label>How far does it reach?</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {Object.entries(SCOPE_LABEL).map(([key, label]) => (
                <button key={key} type="button" onClick={() => setMode(key)}
                        className={`text-xs px-2.5 py-1 rounded-full border ${mode === key ? 'border-indigo-300 bg-indigo-50 text-indigo-800' : 'border-slate-200 text-slate-600'}`}
                        data-testid={`office-scope-${key}`}>
                  {label}
                </button>
              ))}
            </div>
            {mode === 'explicit' && (
              <div className="mt-3" data-testid="office-scope-units">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs text-slate-500">
                    <Building2 className="h-3 w-3 inline mr-1" />{units.length} selected — each one brings everything beneath it
                  </span>
                  <Input value={unitQ} onChange={e => setUnitQ(e.target.value)} placeholder="Search units…"
                         className="h-8 w-40 text-xs" data-testid="office-scope-unit-search" />
                </div>
                <div className="mt-2 max-h-48 overflow-y-auto rounded-lg border border-slate-200 divide-y">
                  {unitRows.map(u => (
                    <label key={u.id} className="flex items-center gap-2 px-3 py-2 text-xs cursor-pointer"
                           data-testid={`office-unit-${u.id}`}>
                      <input type="checkbox" checked={units.includes(u.id)}
                             onChange={() => toggle(units, setUnits, u.id)} />
                      <span className="font-medium text-slate-700">{u.name}</span>
                      {u.level && <span className="text-slate-400">· {u.level}</span>}
                    </label>
                  ))}
                  {unitRows.length === 0 && <p className="px-3 py-3 text-xs text-slate-400">Nothing matches.</p>}
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending} data-testid="office-save">
            {save.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
